import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  Flex,
  Image,
  SimpleGrid,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  ViewEInvoiceData,
  DownloadEInvoicePDF,
  CorrectRecipients,
  CreditInvoice,
} from 'reactRedux/thunks/eInvoiceThunk/eInvoiceThunk';
import PageLoader from 'components/wholePageLoader/PageLoader';
import moment from 'moment';
import { FaEdit, FaFilePdf, FaCreditCard } from 'react-icons/fa';
import { useForm } from 'react-hook-form';
import CommonInput from 'components/commonInput/CommonInput';
import { useColorModeVariables } from 'components/colorModeVarialbels/ColorModeValues';
import BackButton from 'components/backButton/BackButton';
import { sideBarRoutes } from 'utils/StaticData';
import Swal from 'sweetalert2';

const EInvoiceDetail = () => {
  const { invoiceDetailId } = useParams();
  const invoiceType = 'B2B'; // Merchant invoices
  const dispatch = useDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { textColor, brandStars } = useColorModeVariables();
  const {
    viewEInvoiceDetails,
    isViewLoading,
    isDownloadLoading,
    isCorrectLoading,
    isCreditLoading,
  } = useSelector((state) => state.EInvoiceSlice);
  const { adminDetails } = useSelector((state) => state.authSlice);

  const isAdmin =
    adminDetails?.role === sideBarRoutes.Admin ||
    adminDetails?.role === 1 ||
    adminDetails?.role === '1';

  // Get region ID from invoice details
  const regionId = viewEInvoiceDetails?.jsonData?.regionData?.regionId;

  // Merchant invoices store the currency symbol under regionData (not top-level)
  const currencySymbol =
    viewEInvoiceDetails?.jsonData?.regionData?.currencySymbol ?? '€';

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      merchantName: '',
      address: '',
      postalCode: '',
      taxNumber: '',
    },
  });

  useEffect(() => {
    if (invoiceDetailId) {
      dispatch(ViewEInvoiceData({ id: invoiceDetailId, invoiceType }));
    }
  }, [invoiceDetailId, invoiceType, dispatch]);

  const handleDownloadPDF = () => {
    dispatch(
      DownloadEInvoicePDF({
        id: invoiceDetailId,
        invoiceType: invoiceType,
        invoiceId: viewEInvoiceDetails?.invoiceId,
      }),
    );
  };

  // Populate form when modal opens
  useEffect(() => {
    if (isOpen && viewEInvoiceDetails) {
      const formData = {
        merchantName:
          viewEInvoiceDetails?.jsonData?.fiskalyResponse?.metadata
            ?.legal_name ||
          viewEInvoiceDetails?.jsonData?.merchant?.name ||
          '',
        address:
          viewEInvoiceDetails?.jsonData?.fiskalyResponse?.metadata
            ?.legal_address ||
          viewEInvoiceDetails?.jsonData?.merchant?.address ||
          '',
        postalCode:
          viewEInvoiceDetails?.jsonData?.fiskalyResponse?.metadata
            ?.postal_code || '',
        taxNumber:
          viewEInvoiceDetails?.jsonData?.fiskalyResponse?.metadata
            ?.tax_number || '',
      };

      reset(formData);
    }
  }, [isOpen, viewEInvoiceDetails, reset]);

  const onSubmit = (data) => {
    dispatch(
      CorrectRecipients({
        id: invoiceDetailId,
        data,
        callback: () => {
          onClose();
          dispatch(ViewEInvoiceData({ id: invoiceDetailId }));
        },
      }),
    );
  };

  const handleEditClick = () => {
    onOpen();
  };

  const handleCredit = async () => {
    const result = await Swal.fire({
      title: 'Credit this invoice?',
      text: 'This will create a credit note and cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Credit',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    dispatch(
      CreditInvoice({
        id: invoiceDetailId,
        callback: () => {
          dispatch(ViewEInvoiceData({ id: invoiceDetailId, invoiceType }));
        },
        errorCallback: () => {
          dispatch(ViewEInvoiceData({ id: invoiceDetailId, invoiceType }));
        },
      }),
    );
  };

  const DetailItem = ({ label, value }) => (
    <Flex
      alignItems={{ sm: 'start', md: 'center' }}
      direction={{ sm: 'column', md: 'row' }}
      gap="10px"
      padding="4px 8px"
      borderLeft={'1px solid #ccc'}
    >
      <Text fontWeight="normal">{label} :</Text>
      <Text fontWeight="semibold" wordBreak="break-word">
        {value || 'N/A'}
      </Text>
    </Flex>
  );
  DetailItem.propTypes = {
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  };

  const qrData =
    viewEInvoiceDetails?.jsonData?.fiskalyResponse?.content?.compliance?.code
      ?.image?.data;

  return (
    <>
      {isViewLoading ? (
        <PageLoader />
      ) : viewEInvoiceDetails ? (
        <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
          {/* Action Buttons */}
          <Flex justifyContent="space-between" mb={4} gap="3">
            <BackButton
              fallbackUrl="/admin/e-invoices"
              label="Back to E-Invoices"
            />
            <Flex gap="3">
              {!viewEInvoiceDetails?.isCredited &&
                isAdmin &&
                regionId === 2 && (
                  <Button
                    py="10px"
                    px="15px"
                    fontWeight="semibold"
                    borderRadius="8px"
                    bg="primary"
                    color="white"
                    _hover={{ bg: 'primary', opacity: 0.8 }}
                    type="button"
                    leftIcon={<FaEdit size={22} />}
                    onClick={handleEditClick}
                  >
                    Edit Detail
                  </Button>
                )}
              <Button
                py="10px"
                px="15px"
                fontWeight="semibold"
                borderRadius="8px"
                bg="primary"
                color="white"
                _hover={{ bg: 'primary', opacity: 0.8 }}
                type="button"
                leftIcon={<FaFilePdf size={25} />}
                onClick={handleDownloadPDF}
                disabled={isDownloadLoading}
                isLoading={isDownloadLoading}
                loadingText="Downloading..."
              >
                Download PDF
              </Button>
              {!viewEInvoiceDetails?.isCredited &&
                isAdmin &&
                regionId === 2 && (
                  <Button
                    py="10px"
                    px="15px"
                    fontWeight="semibold"
                    borderRadius="8px"
                    bg="primary"
                    color="white"
                    _hover={{ bg: 'primary', opacity: 0.8 }}
                    type="button"
                    leftIcon={<FaCreditCard size={22} />}
                    onClick={handleCredit}
                    disabled={isCreditLoading}
                    isLoading={isCreditLoading}
                    loadingText="Processing..."
                  >
                    Credit Invoice
                  </Button>
                )}
              {qrData && (
                <Image
                  src={`data:image/png;base64,${qrData}`}
                  boxSize="100px"
                  alt="QR Code"
                />
              )}
            </Flex>
          </Flex>

          {/* Edit Merchant Modal */}
          <Modal isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Update Merchant Information</ModalHeader>
              <ModalCloseButton />
              <form onSubmit={handleSubmit(onSubmit)}>
                <ModalBody>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <CommonInput
                      type="text"
                      name="merchantName"
                      labelText="Merchant Name"
                      textColor={textColor}
                      brandStars={brandStars}
                      placeholder="Merchant Name"
                      register={register('merchantName', {
                        required: 'Merchant Name is required',
                      })}
                      errors={errors?.merchantName}
                      isValidate={true}
                    />

                    <CommonInput
                      type="text"
                      name="postalCode"
                      labelText="Postal Code"
                      textColor={textColor}
                      brandStars={brandStars}
                      placeholder="Postal Code"
                      register={register('postalCode', {
                        required: 'Postal Code is required',
                      })}
                      errors={errors?.postalCode}
                      isValidate={true}
                    />

                    <CommonInput
                      type="text"
                      name="address"
                      labelText="Address"
                      textColor={textColor}
                      brandStars={brandStars}
                      placeholder="Address"
                      register={register('address', {
                        required: 'Address is required',
                      })}
                      errors={errors?.address}
                      isValidate={true}
                    />

                    <CommonInput
                      type="text"
                      name="taxNumber"
                      labelText="Tax Number"
                      textColor={textColor}
                      brandStars={brandStars}
                      placeholder="Tax Number"
                      register={register('taxNumber', {
                        required: 'Tax Number is required',
                      })}
                      errors={errors?.taxNumber}
                      isValidate={true}
                    />
                  </SimpleGrid>
                </ModalBody>

                <ModalFooter>
                  <Button variant="outline" mr={3} onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    bg="primary"
                    color="white"
                    _hover={{ bg: 'primary', opacity: 0.8 }}
                    isLoading={isCorrectLoading}
                    loadingText="Updating..."
                  >
                    Update
                  </Button>
                </ModalFooter>
              </form>
            </ModalContent>
          </Modal>

          {/* E-Invoice Details Card */}
          <Flex direction="column">
            <Card
              w="100%"
              boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
              borderRadius="20px"
              paddingInline="40px"
              paddingBlock="40px"
              overflowX={{ sm: 'scroll', lg: 'hidden' }}
            >
              <SimpleGrid columns={{ base: 1, md: 2, lg: 2 }} spacing={4}>
                <DetailItem
                  label="E-Invoice ID"
                  value={viewEInvoiceDetails?.invoiceId}
                />
                <DetailItem
                  label="Amount"
                  value={
                    viewEInvoiceDetails?.jsonData?.commission !== undefined
                      ? `${currencySymbol}${Number(viewEInvoiceDetails.jsonData.commission).toFixed(2)}`
                      : 'N/A'
                  }
                />
                <DetailItem
                  label="Commission Base Amount"
                  value={
                    viewEInvoiceDetails?.jsonData?.commissionBaseAmount !==
                    undefined
                      ? `${currencySymbol}${Number(viewEInvoiceDetails.jsonData.commissionBaseAmount).toFixed(2)}`
                      : 'N/A'
                  }
                />
                <DetailItem
                  label="Tax Amount"
                  value={
                    viewEInvoiceDetails?.jsonData?.taxAmount !== undefined
                      ? `${currencySymbol}${Number(viewEInvoiceDetails.jsonData.taxAmount).toFixed(2)}`
                      : 'N/A'
                  }
                />
                <DetailItem
                  label="Tax Rate"
                  value={
                    viewEInvoiceDetails?.jsonData?.taxRate
                      ? `${viewEInvoiceDetails.jsonData.taxRate}%`
                      : 'N/A'
                  }
                />
                {viewEInvoiceDetails?.jsonData?.isCreditNote && (
                  <DetailItem
                    label="Credit Amount"
                    value={
                      viewEInvoiceDetails?.jsonData?.creditAmount !== undefined
                        ? `${currencySymbol}${Number(viewEInvoiceDetails.jsonData.creditAmount).toFixed(2)}`
                        : 'N/A'
                    }
                  />
                )}
                {viewEInvoiceDetails?.jsonData?.isCreditNote && (
                  <DetailItem
                    label="Adjusted Commission"
                    value={
                      viewEInvoiceDetails?.jsonData?.adjustedCommission !==
                      undefined
                        ? `${currencySymbol}${Number(viewEInvoiceDetails.jsonData.adjustedCommission).toFixed(2)}`
                        : 'N/A'
                    }
                  />
                )}
                <DetailItem label="Year" value={viewEInvoiceDetails?.year} />
                <DetailItem
                  label="Quarter"
                  value={
                    viewEInvoiceDetails?.quarter
                      ? `Q${viewEInvoiceDetails.quarter}`
                      : 'N/A'
                  }
                />
                <DetailItem
                  label="Invoice Type"
                  value={viewEInvoiceDetails?.invoiceType === 1 ? 'B2B' : 'B2C'}
                />
                <DetailItem
                  label="Created At"
                  value={
                    viewEInvoiceDetails?.createdAt
                      ? moment(viewEInvoiceDetails.createdAt).format(
                          'DD/MM/YYYY',
                        )
                      : 'N/A'
                  }
                />
                <DetailItem
                  label="Status"
                  value={viewEInvoiceDetails?.transmissionStatus || 'N/A'}
                />
                {viewEInvoiceDetails?.jsonData?.previousInvoiceNumber && (
                  <DetailItem
                    label="Reference Invoice Number"
                    value={viewEInvoiceDetails?.jsonData?.previousInvoiceNumber}
                  />
                )}
                {viewEInvoiceDetails?.jsonData?.nextInvoiceNumber && (
                  <DetailItem
                    label="Next Invoice Number"
                    value={viewEInvoiceDetails?.jsonData?.nextInvoiceNumber}
                  />
                )}
              </SimpleGrid>
            </Card>

            {/* Receiver (Merchant) Information */}
            <Card
              w="100%"
              boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
              borderRadius="20px"
              paddingInline="40px"
              paddingBlock="40px"
              overflowX={{ sm: 'scroll', lg: 'hidden' }}
              mt={4}
            >
              <Text fontSize="lg" fontWeight="600" mb={4}>
                Receiver (Merchant) Information
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 2 }} spacing={4}>
                <DetailItem
                  label="Merchant ID"
                  value={viewEInvoiceDetails?.merchantId}
                />
                <DetailItem
                  label="Merchant Name"
                  value={
                    viewEInvoiceDetails?.jsonData?.fiskalyResponse?.metadata
                      ?.legal_name ||
                    viewEInvoiceDetails?.jsonData?.merchant?.name
                  }
                />
                <DetailItem
                  label="Location ID"
                  value={viewEInvoiceDetails?.jsonData?.merchant?.locationId}
                />
                <DetailItem
                  label="Address"
                  value={
                    viewEInvoiceDetails?.jsonData?.fiskalyResponse?.metadata
                      ?.legal_address ||
                    viewEInvoiceDetails?.jsonData?.merchant?.address
                  }
                />
                <DetailItem
                  label="Postal code"
                  value={
                    viewEInvoiceDetails?.jsonData?.fiskalyResponse?.metadata
                      ?.postal_code || 'N/A'
                  }
                />{' '}
                <DetailItem
                  label="Tax number"
                  value={
                    viewEInvoiceDetails?.jsonData?.fiskalyResponse?.metadata
                      ?.tax_number || 'N/A'
                  }
                />
                <DetailItem
                  label="Email"
                  value={
                    viewEInvoiceDetails?.jsonData?.merchant?.merchantEmail
                      ?.split(',')
                      .map((e) => e.trim())
                      .join(', ') || '—'
                  }
                />
                <DetailItem
                  label="Currency"
                  value={viewEInvoiceDetails?.jsonData?.merchant?.currency}
                />
                <DetailItem
                  label="Commission Rate"
                  value={
                    viewEInvoiceDetails?.jsonData?.merchant?.commission
                      ? `${viewEInvoiceDetails.jsonData.merchant.commission}%`
                      : 'N/A'
                  }
                />
              </SimpleGrid>
            </Card>

            {/* Sender (Organization) Information */}
            <Card
              w="100%"
              boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
              borderRadius="20px"
              paddingInline="40px"
              paddingBlock="40px"
              overflowX={{ sm: 'scroll', lg: 'hidden' }}
              mt={4}
            >
              <Text fontSize="lg" fontWeight="600" mb={4}>
                Sender (Organization) Information
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 2 }} spacing={4}>
                <DetailItem
                  label="Name"
                  value={viewEInvoiceDetails?.jsonData?.regionData?.orgName}
                />
                <DetailItem
                  label="Region"
                  value={viewEInvoiceDetails?.jsonData?.regionData?.regionName}
                />
                <DetailItem
                  label="Address"
                  value={viewEInvoiceDetails?.jsonData?.regionData?.orgAddress}
                />
                <DetailItem
                  label="Landmark"
                  value={viewEInvoiceDetails?.jsonData?.regionData?.landmark}
                />
              </SimpleGrid>
            </Card>
          </Flex>
        </Box>
      ) : (
        <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
          <Card p={6} width="100%">
            <Text textAlign="center" color="gray.500">
              No invoice details found
            </Text>
          </Card>
        </Box>
      )}
    </>
  );
};

export default EInvoiceDetail;
